import { useState, useEffect } from "react";
import { getSectionBrands } from "../../services/brandService";

// ─── Config ───────────────────────────────────────────────────────────────────
const CONSTANT = 11.664; // 1 tola in grams
const GOLD_CATEGORIES = [
  { key: "pc", label: "PC" },
  { key: "lab", label: "Lab", noBrands: true },
  { key: "bar", label: "Bar" },
  { key: "coin", label: "Coin" },
  { key: "ginni", label: "Ginni", noBrands: true },
];
const SILVER_CATEGORIES = [{ key: "silver", label: "Silver" }];

const INITIAL_FORM = {
  partnerId: "plzSelect",
  dateTime: new Date().toISOString().slice(0, 16),
  type: "plzSelect",
  cashAmount: "",
  notes: "",
};

const makeRow = (noBrands = false) => ({
  id: Math.random().toString(36).slice(2),
  brand: "plzSelect",
  brandId: "",
  brandName: "",
  qty: "1",
  weight: "",
  unit: "gram",
  rate: "",
  note: "",
  noBrands,
});

// ─── Brand cache ──────────────────────────────────────────────────────────────
const brandCache = {};
async function fetchBrands(section) {
  if (brandCache[section]) return brandCache[section];
  try {
    const response = await getSectionBrands(section);
    if (response.success) {
      brandCache[section] = response.brands || [];
      return brandCache[section];
    }
    return [];
  } catch {
    return [];
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const toGrams = (w, unit) =>
  (parseFloat(w) || 0) * (unit === "tola" ? 11.664 : 1);
const rowAmount = (row) =>
  toGrams(row.weight / CONSTANT, row.unit) * (parseFloat(row.rate) || 0);

// ─── Prefill helpers (edit mode) ──────────────────────────────────────────────

// Build form state from an existing investment document
function buildFormFromEdit(inv) {
  return {
    partnerId: inv.partnerId?._id ?? inv.partnerId ?? "plzSelect",
    dateTime: inv.dateTime
      ? new Date(inv.dateTime).toISOString().slice(0, 16)
      : new Date().toISOString().slice(0, 16),
    type: inv.type ?? "plzSelect",
    cashAmount: inv.cashAmount ? String(inv.cashAmount) : "",
    notes: inv.notes ?? "",
  };
}

// Build categories state from an existing investment document
function buildCategoriesFromEdit(inv) {
  if (!inv.categories?.length) return [];

  const allCats = [...GOLD_CATEGORIES, ...SILVER_CATEGORIES];

  return inv.categories.map((cat) => {
    const catConfig = allCats.find((c) => c.key === cat.catKey);
    const noBrands = catConfig?.noBrands ?? false;

    const rows = (cat.rows ?? []).map((r) => {
      // your schema stores brandId + brandName as flat fields
      // r.brand may be a populated object OR undefined — handle both
      const brandId = r.brandId ?? r.brand?._id ?? "";
      const brandName = r.brandName ?? r.brand?.name ?? "";

      return {
        id: Math.random().toString(36).slice(2),
        brand: brandId || "plzSelect", // ← select value
        brandId,
        brandName,
        qty: String(r.qty ?? 1),
        weight: String(r.weight ?? ""),
        unit: r.unit ?? "gram",
        rate: String(r.rate ?? ""),
        note: r.note ?? "",
        noBrands,
      };
    });

    return { catKey: cat.catKey, rows };
  });
}

// ─── Field wrapper ────────────────────────────────────────────────────────────
function Field({ label, required, error, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {label && (
        <label className="form-label" style={{ fontSize: 12, marginBottom: 3 }}>
          {label}
          {required && <span className="text-danger ms-1">*</span>}
        </label>
      )}
      {children}
      <div style={{ minHeight: 18, marginTop: 2 }}>
        {error && (
          <span style={{ fontSize: 11, color: "#dc3545", lineHeight: 1.2 }}>
            {error}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Step bar ─────────────────────────────────────────────────────────────────
function StepBar({ step }) {
  const steps = ["Investment Info", "Where to Invest"];
  return (
    <div className="d-flex align-items-center mb-4 px-1">
      {steps.map((label, i) => {
        const num = i + 1;
        const active = step === num;
        const done = step > num;
        return (
          <div
            key={num}
            className="d-flex align-items-center"
            style={{ flex: i < steps.length - 1 ? 1 : "none" }}
          >
            <div className="d-flex flex-column align-items-center">
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13,
                  fontWeight: 700,
                  flexShrink: 0,
                  background: done ? "#198754" : active ? "#0d6efd" : "#e9ecef",
                  color: done || active ? "#fff" : "#6c757d",
                  transition: "background 0.2s",
                }}
              >
                {done ? "✓" : num}
              </div>
              <span
                style={{
                  fontSize: 11,
                  marginTop: 4,
                  whiteSpace: "nowrap",
                  color: active ? "#0d6efd" : done ? "#198754" : "#6c757d",
                  fontWeight: active ? 600 : 400,
                }}
              >
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                style={{
                  flex: 1,
                  height: 2,
                  margin: "0 8px",
                  marginBottom: 20,
                  background: done ? "#198754" : "#e9ecef",
                  transition: "background 0.2s",
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Brand row ────────────────────────────────────────────────────────────────
function BrandRow({
  row,
  brands,
  onChange,
  onRemove,
  canRemove,
  errors,
  noBrands,
  onClearError,
}) {
  const stopScroll = (e) => e.target.blur();
  const set = (field, val) => {
    onChange({ ...row, [field]: val });
    if (errors?.[field]) onClearError(field);
  };

  const grams = toGrams(row.weight, row.unit);
  const amount = (grams / CONSTANT) * (parseFloat(row.rate) || 0);

  return (
    <div
      className="p-2 rounded mb-2"
      style={{
        background: "#fff",
        border: `1px solid ${Object.keys(errors || {}).length ? "#f5c2c7" : "#dee2e6"}`,
        transition: "border-color 0.2s",
      }}
    >
      {/* Row 1: Brand / Qty / Weight / Unit */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: noBrands ? "2fr 3fr 2fr" : "3fr 1.5fr 2.5fr 2fr",
          gap: 8,
          marginBottom: 4,
        }}
      >
        {!noBrands && (
          <Field label="Brand" required error={errors?.brand}>
            <select
              className={`form-control form-control-sm ${errors?.brand ? "is-invalid" : ""}`}
              value={row.brand}
              onChange={(e) => {
                const selected = brands.find((b) => b._id === e.target.value);
                onChange({
                  ...row,
                  brand: e.target.value,
                  brandId: e.target.value,
                  brandName: selected?.name ?? "",
                });
                if (errors?.brand) onClearError("brand");
              }}
            >
              <option value="plzSelect">--- Select ---</option>

              {/* show saved brand while API loads so select isn't blank */}
              {brands.length === 0 && row.brandId && row.brandName && (
                <option value={row.brandId}>{row.brandName}</option>
              )}

              {brands.map((b) => (
                <option key={b._id} value={b._id}>
                  {b.name}
                </option>
              ))}
            </select>
          </Field>
        )}

        <Field label="Qty" required error={errors?.qty}>
          <input
            type="number"
            className={`form-control form-control-sm ${errors?.qty ? "is-invalid" : ""}`}
            placeholder="1"
            min={1}
            step={1}
            value={row.qty}
            onWheel={stopScroll}
            onChange={(e) => set("qty", e.target.value)}
          />
        </Field>

        <Field label="Weight" required error={errors?.weight}>
          <input
            type="number"
            className={`form-control form-control-sm ${errors?.weight ? "is-invalid" : ""}`}
            placeholder="0.000"
            min={0}
            step="0.001"
            value={row.weight}
            onWheel={stopScroll}
            onChange={(e) => set("weight", e.target.value)}
          />
        </Field>

        <Field label="Unit">
          <select
            className="form-control form-control-sm"
            value={row.unit}
            onChange={(e) => set("unit", e.target.value)}
          >
            <option value="gram">gram</option>
            <option value="tola">tola</option>
          </select>
        </Field>
      </div>

      {/* Row 2: Rate / Amount / Remove */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr auto",
          gap: 8,
          alignItems: "end",
          marginBottom: 4,
        }}
      >
        <Field label="Rate — PKR/tola" required error={errors?.rate}>
          <input
            type="number"
            className={`form-control form-control-sm ${errors?.rate ? "is-invalid" : ""}`}
            placeholder="e.g. 8500"
            min={0}
            step="1"
            value={row.rate}
            onWheel={stopScroll}
            onChange={(e) => set("rate", e.target.value)}
          />
        </Field>

        <Field label="Amount (auto)">
          <input
            type="text"
            className="form-control form-control-sm"
            readOnly
            value={
              amount > 0
                ? `Rs. ${amount.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : "—"
            }
            style={{
              background: "#f8f9fa",
              color: amount > 0 ? "#198754" : "#adb5bd",
              fontWeight: 600,
            }}
          />
        </Field>

        <div style={{ paddingBottom: 20 }}>
          {canRemove ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              style={{ fontSize: 11, padding: "4px 8px" }}
              onClick={onRemove}
              title="Remove row"
            >
              ✕
            </button>
          ) : (
            <div style={{ width: 31 }} />
          )}
        </div>
      </div>

      {/* Row 3: Note */}
      <input
        type="text"
        className="form-control form-control-sm"
        placeholder="Note (optional)"
        value={row.note}
        onChange={(e) => set("note", e.target.value)}
      />
    </div>
  );
}

// ─── Category block ───────────────────────────────────────────────────────────
function CategoryBlock({
  cat,
  rows,
  brands,
  onChange,
  onRemove,
  rowErrors,
  onClearRowError,
}) {
  const addRow = () => onChange([...rows, makeRow(cat.noBrands ?? false)]);
  const updateRow = (idx, upd) =>
    onChange(rows.map((r, i) => (i === idx ? upd : r)));
  const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));

  const totalG = rows.reduce((s, r) => s + toGrams(r.weight, r.unit), 0);
  const totalAmt = rows.reduce((s, r) => s + rowAmount(r), 0);

  return (
    <div
      className="mb-3 p-3 rounded"
      style={{ border: "1px solid #c2d3f8", background: "#f0f4ff" }}
    >
      <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-1">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span
            className="fw-semibold"
            style={{ fontSize: 14, color: "#0d6efd" }}
          >
            {cat.label}
          </span>
          {totalG > 0 && (
            <span
              style={{
                fontSize: 11,
                background: "#dbe8ff",
                color: "#0d6efd",
                borderRadius: 10,
                padding: "1px 8px",
              }}
            >
              {totalG.toFixed(3)} g
            </span>
          )}
          {totalAmt > 0 && (
            <span
              style={{
                fontSize: 11,
                background: "#d1fae5",
                color: "#065f46",
                borderRadius: 10,
                padding: "1px 8px",
              }}
            >
              Rs.{" "}
              {totalAmt.toLocaleString("en-PK", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          )}
        </div>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            style={{ fontSize: 12, padding: "2px 10px" }}
            onClick={addRow}
          >
            + Brand
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            style={{ fontSize: 12, padding: "2px 8px" }}
            onClick={onRemove}
          >
            ✕
          </button>
        </div>
      </div>

      {rows.map((row, idx) => (
        <BrandRow
          key={row.id}
          row={row}
          brands={brands}
          noBrands={cat.noBrands}
          onChange={(upd) => updateRow(idx, upd)}
          onRemove={() => removeRow(idx)}
          canRemove={rows.length > 1}
          errors={rowErrors?.[row.id]}
          onClearError={(field) => onClearRowError(row.id, field)}
        />
      ))}
    </div>
  );
}

// ─── Step 2 ───────────────────────────────────────────────────────────────────
function StepAllocation({
  type,
  categories,
  onChange,
  rowErrors,
  onClearRowError,
  destError,
}) {
  const catList = type === "gold" ? GOLD_CATEGORIES : SILVER_CATEGORIES;
  const addedKeys = categories.map((c) => c.catKey);
  const [brandMap, setBrandMap] = useState({});

  useEffect(() => {
    // fetch for catList (add mode) + already-added categories (edit mode)
    const keysToLoad = [
      ...catList.map((c) => c.key),
      ...categories.map((c) => c.catKey),
    ];

    [...new Set(keysToLoad)].forEach(async (key) => {
      if (!brandMap[key]) {
        const brands = await fetchBrands(key);
        setBrandMap((p) => ({ ...p, [key]: brands }));
      }
    });
  }, [type, categories.map((c) => c.catKey).join(",")]); // ← add categories dependency

  useEffect(() => {
    catList.forEach(async ({ key }) => {
      if (!brandMap[key]) {
        const brands = await fetchBrands(key);
        setBrandMap((p) => ({ ...p, [key]: brands }));
      }
    });
  }, [type]);

  const addCategory = (catKey) => {
    if (addedKeys.includes(catKey)) return;
    const cat = catList.find((c) => c.key === catKey);
    onChange([
      ...categories,
      { catKey, rows: [makeRow(cat.noBrands ?? false)] },
    ]);
  };
  const updateRows = (catKey, rows) =>
    onChange(categories.map((c) => (c.catKey === catKey ? { ...c, rows } : c)));
  const removeCategory = (catKey) =>
    onChange(categories.filter((c) => c.catKey !== catKey));

  const allRows = categories.flatMap((c) => c.rows);
  const grandG = allRows.reduce((s, r) => s + toGrams(r.weight, r.unit), 0);
  const grandAmt = allRows.reduce((s, r) => s + rowAmount(r), 0);

  return (
    <div>
      <p className="text-muted mb-2" style={{ fontSize: 13 }}>
        Select categories to invest in:
      </p>
      <div className="d-flex flex-wrap gap-2 mb-3">
        {catList.map((cat) => {
          const added = addedKeys.includes(cat.key);
          return (
            <button
              key={cat.key}
              type="button"
              className={`btn btn-sm ${added ? "btn-primary" : "btn-outline-secondary"}`}
              style={{ fontSize: 13, borderRadius: 20, padding: "3px 14px" }}
              onClick={() => !added && addCategory(cat.key)}
              disabled={added}
            >
              {added ? `✓ ${cat.label}` : `+ ${cat.label}`}
            </button>
          );
        })}
      </div>

      {categories.length === 0 && (
        <p
          className="text-muted text-center py-3 mb-0"
          style={{ fontSize: 13 }}
        >
          Tap a category above to begin.
        </p>
      )}

      {categories.map(({ catKey, rows }) => {
        const cat = catList.find((c) => c.key === catKey);
        const brands = brandMap[catKey] ?? [];
        return (
          <CategoryBlock
            key={catKey}
            cat={cat}
            rows={rows}
            brands={brands}
            onChange={(upd) => updateRows(catKey, upd)}
            onRemove={() => removeCategory(catKey)}
            rowErrors={rowErrors}
            onClearRowError={onClearRowError}
          />
        );
      })}

      {(grandG > 0 || grandAmt > 0) && (
        <div
          className="p-2 rounded mt-1"
          style={{ background: "#e8f5e9", border: "1px solid #c8e6c9" }}
        >
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <span className="fw-semibold" style={{ fontSize: 13 }}>
              Total Investment
            </span>
            <div className="d-flex gap-3">
              <span style={{ fontSize: 13 }}>
                <span className="text-muted">Weight:</span>&nbsp;
                <strong style={{ color: "#0d6efd" }}>
                  {grandG.toFixed(3)} g
                </strong>
              </span>
              <span style={{ fontSize: 13 }}>
                <span className="text-muted">Amount:</span>&nbsp;
                <strong style={{ color: "#198754" }}>
                  Rs.{" "}
                  {grandAmt.toLocaleString("en-PK", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </strong>
              </span>
            </div>
          </div>
        </div>
      )}

      {destError && (
        <div
          className="alert alert-danger py-2 mt-2 mb-0"
          style={{ fontSize: 13 }}
        >
          ⚠ {destError}
        </div>
      )}
    </div>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────
export default function AddInvestmentModal({
  show,
  onClose,
  onSave, // (payload) => Promise<true>  — used for CREATE
  onUpdate, // (id, payload) => Promise<true>  — used for EDIT
  partners = [],
  isLoading = false,
  editData = null, // existing investment doc → triggers edit mode
}) {
  const isEditMode = !!editData?._id;

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(INITIAL_FORM);
  const [categories, setCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [rowErrors, setRowErrors] = useState({});

  // ── Prefill when editData changes (modal opened in edit mode) ───────────────
  useEffect(() => {
    if (show && isEditMode) {
      setForm(buildFormFromEdit(editData));
      setCategories(buildCategoriesFromEdit(editData));
      setStep(1);
      setErrors({});
      setRowErrors({});
    } else if (show && !isEditMode) {
      // opening in create mode — reset everything
      resetAll();
    }
  }, [show, editData?._id]); // only re-run when the modal opens or the target changes

  // ── Reset categories when type changes (create mode only) ──────────────────
  useEffect(() => {
    if (!isEditMode) setCategories([]);
  }, [form.type]);

  const isMetal = form.type === "gold" || form.type === "silver";
  const isCash = form.type === "cash";

  const handleChange = (e) => {
    const { id, value } = e.target;
    setForm((p) => ({ ...p, [id]: value }));
    setErrors((p) => {
      if (!p[id]) return p;
      const next = { ...p };
      delete next[id];
      return next;
    });
  };

  const clearRowError = (rowId, field) => {
    setRowErrors((prev) => {
      if (!prev[rowId]?.[field]) return prev;
      const next = { ...prev };
      const rowNext = { ...next[rowId] };
      delete rowNext[field];
      if (Object.keys(rowNext).length === 0) delete next[rowId];
      else next[rowId] = rowNext;
      return next;
    });
  };

  // ── Validation ──────────────────────────────────────────────────────────────
  const validateStep1 = () => {
    const e = {};
    if (form.partnerId === "plzSelect")
      e.partnerId = "Please select a partner.";
    if (form.type === "plzSelect") e.type = "Please select an investment type.";
    if (isCash && (!form.cashAmount || parseFloat(form.cashAmount) <= 0))
      e.cashAmount = "Please enter an amount.";
    return e;
  };

  const validateStep2 = () => {
    const e = {},
      rErrs = {};
    if (categories.length === 0) {
      e.destination = "Add at least one category.";
      return { e, rErrs };
    }
    categories.forEach(({ rows }) => {
      rows.forEach((row) => {
        const rowE = {};
        if (!row?.noBrands && (!row.brand || row.brand === "plzSelect"))
          rowE.brand = "Select a brand.";
        if (!row.qty || parseInt(row.qty) <= 0) rowE.qty = "Enter qty.";
        if (!row.weight || parseFloat(row.weight) <= 0)
          rowE.weight = "Enter weight.";
        if (!row.rate || parseFloat(row.rate) <= 0) rowE.rate = "Enter rate.";
        if (Object.keys(rowE).length) rErrs[row.id] = rowE;
      });
    });
    if (Object.keys(rErrs).length)
      e.destination = "Fix the errors in the rows below.";
    return { e, rErrs };
  };

  const goNext = () => {
    const e = validateStep1();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setErrors({});
    setStep(2);
  };

  const goBack = () => {
    setErrors({});
    setRowErrors({});
    setStep(1);
  };

  // ── Save (create or update) ─────────────────────────────────────────────────
  const handleSave = async () => {
    if (isMetal) {
      const { e, rErrs } = validateStep2();
      setRowErrors(rErrs);
      if (Object.keys(e).length) {
        setErrors((p) => ({ ...p, ...e }));
        return;
      }
    } else {
      const e = validateStep1();
      if (Object.keys(e).length) {
        setErrors(e);
        return;
      }
    }

    const allRows = categories.flatMap((c) => c.rows);
    const totalWeight = allRows.reduce(
      (s, r) => s + toGrams(r.weight, r.unit),
      0,
    );
    const totalAmount = allRows.reduce((s, r) => s + rowAmount(r), 0);

    const payload = {
      ...form,
      ...(isMetal ? { categories, totalWeight, totalAmount } : {}),
    };

    let ok = false;

    if (isEditMode) {
      // ── EDIT — call onUpdate with the investment id ─────────────────────────
      if (onUpdate) ok = await onUpdate(editData._id, payload);
    } else {
      // ── CREATE — call onSave as before ──────────────────────────────────────
      if (onSave) ok = await onSave(payload);
    }

    if (ok) {
      resetAll();
      onClose();
    } else {
      setErrors({
        global: isEditMode
          ? "Couldn't update the investment. Please try again."
          : "Couldn't create the investment. Please try again.",
      });
    }
  };

  const resetAll = () => {
    setForm(INITIAL_FORM);
    setCategories([]);
    setErrors({});
    setRowErrors({});
    setStep(1);
  };

  const handleClose = () => {
    resetAll();
    onClose();
  };

  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.5)",
        overflowY: "auto",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        padding: "4rem 1rem 2rem",
      }}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 640,
          background: "#fff",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        {/* HEADER */}
        <div className="modal-header">
          <h1 className="modal-title fs-5">
            {isEditMode ? "Edit Investment" : "Add Investment"}
          </h1>
          {/* edit mode pill */}
          {isEditMode && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                background: "#fff8e1",
                color: "#b45309",
                border: "1px solid #fde68a",
                borderRadius: 6,
                padding: "2px 10px",
                marginLeft: 8,
              }}
            >
              Editing
            </span>
          )}
          <button className="btn-close ms-auto" onClick={handleClose} />
        </div>

        {/* BODY */}
        <div className="modal-body">
          {errors.global && (
            <div className="alert alert-danger py-2 mb-3">{errors.global}</div>
          )}

          {isMetal && <StepBar step={step} />}

          {/* STEP 1 */}
          {step === 1 && (
            <div className="row g-3">
              <div className="col-4">
                <label className="form-label">Select Partner</label>
                <select
                  id="partnerId"
                  className={`form-control ${errors.partnerId ? "is-invalid" : ""}`}
                  value={form.partnerId}
                  onChange={handleChange}
                >
                  <option value="plzSelect">--- Select ---</option>
                  {partners.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                {errors.partnerId && (
                  <div className="invalid-feedback">{errors.partnerId}</div>
                )}
              </div>

              <div className="col-4">
                <label className="form-label">Date / Time</label>
                <input
                  type="datetime-local"
                  id="dateTime"
                  className="form-control"
                  value={form.dateTime}
                  onChange={handleChange}
                />
              </div>

              <div className="col-4">
                <label className="form-label">Investment Type</label>
                <select
                  id="type"
                  className={`form-control ${errors.type ? "is-invalid" : ""}`}
                  value={form.type}
                  onChange={handleChange}
                  // lock type in edit mode to avoid complex category mismatch
                  disabled={isEditMode}
                >
                  <option value="plzSelect">--- Select ---</option>
                  <option value="gold">Gold</option>
                  <option value="silver">Silver</option>
                  <option value="cash">Cash</option>
                </select>
                {isEditMode && (
                  <div className="form-text" style={{ fontSize: 11 }}>
                    Type cannot be changed on edit.
                  </div>
                )}
                {errors.type && (
                  <div className="invalid-feedback">{errors.type}</div>
                )}
              </div>

              {isCash && (
                <div className="col-4">
                  <label className="form-label">Amount (PKR)</label>
                  <input
                    type="number"
                    id="cashAmount"
                    className={`form-control ${errors.cashAmount ? "is-invalid" : ""}`}
                    placeholder="e.g. 600000"
                    value={form.cashAmount ?? ""}
                    onChange={handleChange}
                    min={0}
                    onWheel={(e) => e.target.blur()}
                  />
                  {errors.cashAmount && (
                    <div className="invalid-feedback">{errors.cashAmount}</div>
                  )}
                </div>
              )}

              <div className={isCash ? "col-8" : "col-12"}>
                <label className="form-label">Notes</label>
                <input
                  type="text"
                  id="notes"
                  className="form-control"
                  placeholder="Add a note"
                  value={form.notes}
                  onChange={handleChange}
                />
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <>
              <div
                className="d-flex gap-3 p-2 rounded mb-3"
                style={{
                  background: "#e8f0fe",
                  border: "1px solid #c2d3f8",
                  fontSize: 13,
                }}
              >
                <span>
                  <span className="text-muted">Type:</span>&nbsp;
                  <strong style={{ textTransform: "capitalize" }}>
                    {form.type}
                  </strong>
                </span>
                <span className="text-muted" style={{ fontSize: 12 }}>
                  Rate entered per row below
                </span>
              </div>

              <StepAllocation
                type={form.type}
                categories={categories}
                onChange={setCategories}
                rowErrors={rowErrors}
                onClearRowError={clearRowError}
                destError={errors.destination}
              />
            </>
          )}
        </div>

        {/* FOOTER */}
        <div className="modal-footer m-2 gap-2">
          {step === 2 ? (
            <button className="btn btn-outline-secondary" onClick={goBack}>
              ← Back
            </button>
          ) : (
            <button className="btn btn-secondary" onClick={handleClose}>
              Close
            </button>
          )}
          {step === 1 ? (
            <button
              className="btn btn-primary"
              onClick={isMetal ? goNext : handleSave}
            >
              {isMetal
                ? "Next →"
                : isLoading
                  ? "Saving..."
                  : isEditMode
                    ? "Save Changes"
                    : "Save"}
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading
                ? "Saving..."
                : isEditMode
                  ? "Save Changes"
                  : "Save Investment"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
